import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalDataSource, ServerDataSource } from "ng2-smart-table";
import { forkJoin, from, Observable, of } from "rxjs";
import { SpringServerDataSource } from "../../masarat/shared/service/spring-server-dataSource.service";
import { map, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { ApiEndpoints } from "../../masarat/shared/config/api-endpoints.interface";
import { AppConfigService } from "../../masarat/shared/config/app-config.service";

@Injectable()
export abstract class SmartTableData {
  dataKeyName = "refId"; 
  protected page = 0;
  protected size = 10;
  protected toFlatFormat: boolean | string[];
  protected fetchEnumList: string[];
  protected apiEndpoints: ApiEndpoints;
  protected apiUrl = environment.apiBaseUrl;

  constructor(protected http: HttpClient, configService: AppConfigService) {
    this.apiEndpoints = configService.getApiEndpoints();
  }

  abstract getEndpoint(params?: any): string;
  //abstract getData(params?: any): Observable<any>;
  
  setQueryParams(endpoint: string, params: any): string {
    if (params) {
      const queryParams = new URLSearchParams();

      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();

      if (queryString) {
        endpoint += `?${queryString}`;
      }
    }

    return endpoint;
  }

  getData(id?: string | number): Observable<any> {
    let params = {};
    if(id){
      params = {id: id};
    } 
    
    const url = this.getEndpoint(params);
    const data = this.http.get(url);

    return data;
  }

  getEnumList(): Observable<any> {
    
    if (!this.fetchEnumList || this.fetchEnumList.length === 0) {
      return of({});
    }

    const apiUrl = this.apiUrl;
    
    const requests = this.fetchEnumList.map(listName => {

      const endpoint = this.apiEndpoints.insProviders.getEnumList
        .replace('${listName}', listName);

      const url = `${apiUrl}/${endpoint}`;
      
      return this.http.get(url).pipe(
        tap(data => {
          // ✅ store as string
          sessionStorage.setItem(listName, JSON.stringify(data));
        }),
        map(data => ({ [listName]: data }))
      );
    });

    return forkJoin(requests).pipe(
      map(results => Object.assign({}, ...results))
    );
  }

  

  createDataSource(params?: any): Observable<ServerDataSource | LocalDataSource> {
    let url = this.getEndpoint(params);

    url = this.setQueryParams(url, params);

    // Make initial HTTP call to detect pagination
    return this.http.get<any>(url, { params: { page: '0', size: String(this.size) } }).pipe(
      map(res => {

        // CASE 1: Spring pageable response
        if (res && Array.isArray(res.content) && res.totalElements !== undefined) {
          const serverSource = new SpringServerDataSource(this.http, {
            endPoint: url,
            dataKey: 'content',
            totalKey: 'totalElements',
            pagerPageKey: 'page',
            pagerLimitKey: 'size',
            sortFieldKey: 'sort',
            sortDirKey: 'direction'
          },
          (response) => {
            console.log("response : ", response);
            this.applyFlatten(response);
          } 
        );

          // initialize first page
          serverSource.setPaging(1, this.size, false);

          return serverSource;
        }

        // CASE 2: Non-paginated array
        if (Array.isArray(res)) {
          const localSource = new LocalDataSource();
          res = this.applyFlatten(res);
          console.log("res : ", res);
          localSource.load(res);
          return localSource;
        }

        // CASE 3: Single object
        if (res && typeof res === 'object') {
          const localSource = new LocalDataSource();
          res = this.applyFlatten(res);
          console.log("res : ", res);
          localSource.load([res]);
          return localSource;
        }

        return new LocalDataSource([]);
      })
    );
  }

  

  // Flatten logic
  protected applyFlatten(res: any): any {

    if (!this.toFlatFormat) return res;

    // Spring pageable response
    if (res?.content && Array.isArray(res.content)) {
      res.content = res.content.map(row => this.processRow(row));
      return res;
    }

    // Array response
    if (Array.isArray(res)) {
      return res.map(row => this.processRow(row));
    }

    // Single object
    if (typeof res === 'object') {
      return this.processRow(res);
    }

    return res;
  }

  private processRow(row: any): any {

    if (!this.toFlatFormat) return row;

    const result = { ...row }; // keep original nested objects

    // FULL flatten mode
    if (this.toFlatFormat === true) {

      const flattened = this.flattenObject(row);

      Object.keys(flattened).forEach(key => {
        if (!(key in result)) {
          result[key] = flattened[key];
        }
      });

      return result;
    }

    // PARTIAL flatten
    if (Array.isArray(this.toFlatFormat)) {

      this.toFlatFormat.forEach(field => {

        if (row[field] && typeof row[field] === 'object') {

          const flattened = this.flattenObject(row[field], field);

          Object.assign(result, flattened);

        }

      });

    }

    return result;
  }

  protected flattenObject(obj: any, parentKey: string = '', result: any = {}): any {

    Object.keys(obj).forEach(key => {

      const value = obj[key];
      const newKey = parentKey ? `${parentKey}__${key}` : key;

      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)
      ) {
        this.flattenObject(value, newKey, result);
      } else {
        result[newKey] = value;
      }

    });

    return result;
  }

  getEnumAsList(enumName: string): any[] {
  
    const raw = sessionStorage.getItem(enumName);

    if (!raw) return [];

    try {
      const values: string[] = JSON.parse(raw);

      return values.map(v => ({
        value: v,
        title: this.formatEnumLabel(v)
      }));

    } catch (e) {
      console.error(`Error parsing enum ${enumName}`, e);
      return [];
    }
  }

  private formatEnumLabel(value: string): string {
    return value
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }


}
